import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Card, 
  CardContent, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  IconButton, 
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  FormHelperText,
  FormControlLabel,
  Switch,
  FormGroup,
  FormLabel,
  CircularProgress,
  Select,
  MenuItem,
  InputLabel
} from '@mui/material';
import { 
  Add as AddIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
  Save as SaveIcon,
  Block as BlockIcon,
  FiberManualRecord as FiberManualRecordIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  AccessTime as AccessTimeIcon,
  Inbox as InboxIcon,
  ErrorOutline as ErrorOutlineIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  DragIndicator as DragIndicatorIcon
} from '@mui/icons-material';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, parse } from 'date-fns';
import { es } from 'date-fns/locale';
import { Process, ProcessType, getProcesses, getProcessTypes, createProcess, updateProcess, deleteProcess } from '../../services/processService';
import { User, getActiveUsers } from '../../services/userService';

const initialProcessState: Omit<Process, 'id'> = {
  name: '',
  type: 1,
  status: 'active',
  monday: false,
  tuesday: false,
  wednesday: false,
  thursday: false,
  friday: false,
  saturday: false,
  sunday: false,
  day_time: '00:00',
  emails: ''
};

const daysOfWeek = [
  { id: 'monday', label: 'Lunes' },
  { id: 'tuesday', label: 'Martes' },
  { id: 'wednesday', label: 'Miércoles' },
  { id: 'thursday', label: 'Jueves' },
  { id: 'friday', label: 'Viernes' },
  { id: 'saturday', label: 'Sábado' },
  { id: 'sunday', label: 'Domingo' },
] as const;

const ProcesosPage: React.FC = () => {
  const [processes, setProcesses] = useState<Process[]>([]);
  const [processTypes, setProcessTypes] = useState<ProcessType[]>([]);
  const [activeUsers, setActiveUsers] = useState<User[]>([]);
  const [currentProcess, setCurrentProcess] = useState<Omit<Process, 'id'> & { id?: number }>({
    name: '',
    type: 1,
    status: 'inactive',
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: false,
    sunday: false,
    day_time: '00:00',
    emails: ''
  });
  const [isEditing, setIsEditing] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState<{success: boolean; message: string} | null>(null);
  const [time, setTime] = useState<Date | null>(null);
  const [draggedUser, setDraggedUser] = useState<User | null>(null);

  // Cargar procesos, tipos y usuarios al iniciar
  useEffect(() => {
    fetchProcesses();
    fetchProcessTypes();
    fetchActiveUsers();
  }, []);

  const fetchProcesses = async () => {
    setIsLoading(true);
    try {
      const data = await getProcesses();
      // Asegurar que siempre tengamos un array
      setProcesses(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error al cargar los procesos:', error);
      setProcesses([]); // Establecer array vacío en caso de error
      setSaveStatus({
        success: false,
        message: 'Error al cargar los procesos. Por favor, intente recargar la página.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProcessTypes = async () => {
    try {
      const data = await getProcessTypes();
      console.log('Tipos de proceso cargados:', data);
      // Asegurar que siempre tengamos un array
      const types = Array.isArray(data) ? data : [];
      setProcessTypes(types);
      
      // Si no hay tipos cargados, crear uno por defecto
      if (types.length === 0) {
        console.warn('No se encontraron tipos de proceso, usando tipo por defecto');
        setProcessTypes([{ id: 1, name: 'Proceso General' }]);
      }
    } catch (error) {
      console.error('Error al cargar los tipos de proceso:', error);
      // Establecer tipo por defecto en caso de error
      setProcessTypes([{ id: 1, name: 'Proceso General' }]);
    }
  };

  const fetchActiveUsers = async () => {
    try {
      const users = await getActiveUsers();
      setActiveUsers(users);
    } catch (error) {
      console.error('Error al cargar usuarios activos:', error);
      setActiveUsers([]);
    }
  };

  const handleTimeChange = (newTime: Date | null) => {
    setTime(newTime);
    if (newTime) {
      const formattedTime = format(newTime, 'HH:mm');
      setCurrentProcess(prev => ({
        ...prev,
        day_time: formattedTime
      }));
    } else {
      setCurrentProcess(prev => ({
        ...prev,
        day_time: ''
      }));
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    
    setCurrentProcess(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleEmailsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentProcess(prev => ({
      ...prev,
      emails: e.target.value
    }));
  };

  const addEmailFromUser = (user: User) => {
    const currentEmails = currentProcess.emails || '';
    const emailsArray = currentEmails.split(',').map(email => email.trim()).filter(email => email);
    
    if (!emailsArray.includes(user.email)) {
      const newEmails = emailsArray.length > 0 
        ? `${currentEmails}, ${user.email}`
        : user.email;
      
      setCurrentProcess(prev => ({
        ...prev,
        emails: newEmails
      }));
    }
  };

  const handleDragStart = (e: React.DragEvent, user: User) => {
    setDraggedUser(user);
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (draggedUser) {
      addEmailFromUser(draggedUser);
      setDraggedUser(null);
    }
  };

  const handleDayToggle = (day: keyof Process) => {
    setCurrentProcess(prev => ({
      ...prev,
      [day]: !prev[day]
    }));
  };

  const handleStatusToggle = (process: Process) => {
    const updatedProcess = {
      ...process,
      status: process.status === 'active' ? 'inactive' : 'active' as const
    };
    updateProcess(updatedProcess as Process).then(() => {
      fetchProcesses();
    });
  };

  const handleOpenDialog = (process?: Process) => {
    if (process) {
      setCurrentProcess(process);
      setIsEditing(true);
      // Inicializar el tiempo desde el proceso existente
      if (process.day_time) {
        try {
          const [hours, minutes] = process.day_time.split(':');
          const timeDate = new Date();
          timeDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);
          setTime(timeDate);
        } catch (error) {
          console.error('Error parsing time:', error);
          setTime(null);
        }
      } else {
        setTime(null);
      }
    } else {
      const defaultType = processTypes.length > 0 ? processTypes[0].id : 1;
      setCurrentProcess({
        name: '',
        type: defaultType,
        status: 'inactive',
        monday: false,
        tuesday: false,
        wednesday: false,
        thursday: false,
        friday: false,
        saturday: false,
        sunday: false,
        day_time: '00:00',
        emails: ''
      });
      console.log('Proceso inicializado con tipo:', defaultType);
      setIsEditing(false);
      setTime(null);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSaveStatus(null);
    setTime(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (isEditing && currentProcess.id) {
        await updateProcess(currentProcess as Process);
      } else {
        await createProcess(currentProcess);
      }
      handleCloseDialog();
      fetchProcesses();
    } catch (error) {
      console.error('Error saving process:', error);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('¿Está seguro de que desea eliminar este proceso?')) {
      try {
        await deleteProcess(id);
        setSaveStatus({
          success: true,
          message: 'Proceso eliminado correctamente'
        });
        fetchProcesses();
      } catch (error) {
        console.error('Error al eliminar el proceso:', error);
        setSaveStatus({
          success: false,
          message: 'Error al eliminar el proceso. Por favor, intente nuevamente.'
        });
      }
    }
  };

  const getActiveDays = (process: Process) => {
    return daysOfWeek
      .filter(day => process[day.id as keyof Process])
      .map(day => day.label.substring(0, 3))
      .join(', ');
  };

  // Verifica si hay al menos un día seleccionado
  const hasDaySelected = currentProcess.monday || currentProcess.tuesday || 
    currentProcess.wednesday || currentProcess.thursday || 
    currentProcess.friday || currentProcess.saturday || 
    currentProcess.sunday;

  // Verifica si el formulario es válido (nombre, tipo, al menos un día y hora seleccionados)
  const isFormValid = currentProcess.name.trim() !== '' && 
                      currentProcess.type && currentProcess.type > 0 && 
                      hasDaySelected && 
                      time !== null;
  
  console.log('Validación del formulario:', {
    name: currentProcess.name.trim() !== '',
    type: currentProcess.type && currentProcess.type > 0,
    days: hasDaySelected,
    time: time !== null,
    isValid: isFormValid
  });

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4, px: { xs: 2, sm: 3 } }}>
      <Card sx={{ 
        border: '1px solid', 
        borderColor: 'divider',
        borderRadius: 2,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        overflow: 'hidden',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
        },
        transition: 'all 0.3s ease-in-out'
      }}>
        <Box 
          sx={{
            p: 3,
            borderBottom: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            gap: 2,
            justifyContent: 'space-between'
          }}
        >
          <Box>
            <Typography variant="h4" component="h1" fontWeight="bold" color="text.primary">
              Procesos Automáticos
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              Administra los procesos programados del sistema
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{
              minWidth: '180px',
              alignSelf: { xs: 'stretch', sm: 'center' },
              py: 1.5,
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              '&:hover': {
                boxShadow: '0 4px 8px rgba(0,0,0,0.15)'
              },
              transition: 'all 0.2s ease-in-out'
            }}
          >
            Nuevo Proceso
          </Button>
        </Box>
        
        <Card variant="outlined" sx={{ borderRadius: 0, border: 'none', boxShadow: 'none' }}>
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ p: 3, pb: 2 }}>
              <Typography variant="h5" component="h2" fontWeight="600" color="text.primary" sx={{ fontSize: '1.5rem', mb: 0.5 }}>
                Envío de excel para conciliacion de puntos
              </Typography>
            </Box>
            
            {saveStatus && (
              <Box mb={2}>
                <Typography 
                  color={saveStatus.success ? 'success.main' : 'error.main'}
                  variant="body2"
                >
                  {saveStatus.message}
                </Typography>
              </Box>
            )}
            
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 0, borderLeft: 'none', borderRight: 'none', width: '100%', overflowX: 'auto' }}>
              <Table 
                sx={{ 
                  minWidth: 1200,
                  width: '100%',
                  tableLayout: 'fixed',
                  '& thead .MuiTableCell-root': {
                    backgroundColor: '#f5f5f5',
                    fontWeight: 600,
                  },
                  '& tbody .MuiTableRow-root:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.02)'
                  },
                  '& .MuiTableCell-root': {
                    borderBottom: '1px solid rgba(224, 224, 224, 0.5)',
                    padding: '12px 16px',
                    verticalAlign: 'middle',
                  },
                }} 
                aria-label="tabla de procesos"
              >
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary', width: '20%' }}>Nombre</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary', width: '12%' }}>Tipo</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary', width: '10%' }}>Estado</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary', width: '18%' }}>Días de Ejecución</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary', width: '8%' }}>Hora</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary', width: '25%' }}>Emails</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary', width: '7%' }}>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                        <Box display="flex" flexDirection="column" alignItems="center" py={2}>
                          <CircularProgress size={24} sx={{ mb: 1 }} />
                          <Typography variant="body2" color="text.secondary">
                            Cargando procesos...
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : processes.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <Box display="flex" flexDirection="column" alignItems="center">
                          <InboxIcon color="disabled" sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
                          <Typography variant="body1" color="text.secondary" gutterBottom>
                            No hay procesos registrados
                          </Typography>
                          <Button 
                            variant="outlined" 
                            startIcon={<AddIcon />}
                            onClick={() => handleOpenDialog()}
                            sx={{ mt: 1 }}
                          >
                            Crear primer proceso
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    processes.map((process, index) => (
                      <TableRow 
                        key={process.id}
                        sx={{ 
                          '&:hover': { 
                            backgroundColor: 'action.hover',
                            '& .process-actions': {
                              visibility: 'visible',
                              opacity: 1
                            }
                          },
                          '&:last-child td': { borderBottom: 'none' }
                        }}
                      >
                        <TableCell>
                          <Typography fontWeight={500}>
                            {process.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500} color="text.primary">
                            {processTypes.find(type => type.id === process.type)?.name || `Tipo ${process.type}`}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            {process.status === 'active' ? (
                              <Box
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  px: 1.5,
                                  py: 0.5,
                                  borderRadius: 1,
                                  bgcolor: 'success.main',
                                  color: 'white',
                                  minWidth: '60px',
                                  justifyContent: 'center'
                                }}
                              >
                                <Typography variant="caption" fontWeight={700}>
                                  Activo
                                </Typography>
                              </Box>
                            ) : (
                              <Box
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  px: 1.5,
                                  py: 0.5,
                                  borderRadius: 1,
                                  bgcolor: 'background.paper',
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  color: 'text.secondary',
                                  minWidth: '60px',
                                  justifyContent: 'center'
                                }}
                              >
                                <Typography variant="caption" fontWeight={500}>
                                  Inactivo
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            {daysOfWeek.map(day => (
                              process[day.id as keyof Process] && (
                                <Tooltip key={day.id} title={day.label}>
                                  <Box 
                                    sx={{
                                      minWidth: 32,
                                      height: 32,
                                      borderRadius: '16px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      bgcolor: process.status === 'active' 
                                        ? (process[day.id as keyof Process] ? 'primary.main' : 'action.selected')
                                        : (process[day.id as keyof Process] ? 'action.selected' : 'background.paper'),
                                      color: process.status === 'active'
                                        ? (process[day.id as keyof Process] ? 'primary.contrastText' : 'text.secondary')
                                        : 'text.secondary',
                                      border: process.status === 'inactive' ? '1px solid' : 'none',
                                      borderColor: process.status === 'inactive' ? 'divider' : 'transparent',
                                      opacity: process.status === 'inactive' ? 0.7 : 1,
                                      fontWeight: 500,
                                      fontSize: '0.75rem',
                                      padding: '0 6px',
                                      transition: 'all 0.2s ease-in-out'
                                    }}
                                  >
                                    {day.label.substring(0, 3)}
                                  </Box>
                                </Tooltip>
                              )
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box 
                            bgcolor="action.selected" 
                            display="inline-block" 
                            px={1.5} 
                            py={0.5} 
                            borderRadius={1}
                          >
                            <Typography variant="body2" fontWeight={500}>
                              {process.day_time ? process.day_time.split(':').slice(0, 2).join(':') : '--:--'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ width: '100%' }}>
                            {process.emails ? (
                              <Typography variant="body2" sx={{ 
                                wordBreak: 'break-word',
                                fontSize: '0.8rem',
                                color: 'text.secondary',
                                lineHeight: 1.3,
                                whiteSpace: 'normal'
                              }}>
                                {process.emails}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                                Sin emails
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box 
                            className="process-actions"
                            sx={{
                              display: 'flex',
                              gap: 0.5,
                              justifyContent: 'center'
                            }}
                          >
                            <Tooltip title="Editar">
                              <IconButton 
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenDialog(process);
                                }}
                                sx={{ 
                                  color: 'grey.700',
                                  '&:hover': { 
                                    color: 'primary.main',
                                    bgcolor: 'primary.light'
                                  } 
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Eliminar">
                              <IconButton 
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  process.id && handleDelete(process.id);
                                }}
                                sx={{ 
                                  color: 'grey.700',
                                  '&:hover': { 
                                    color: 'error.main',
                                    bgcolor: 'error.light'
                                  } 
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Card>

      {/* Diálogo para crear/editar proceso */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
          }
        }}
      >
        <form onSubmit={handleSubmit}>
          <DialogTitle sx={{ 
            bgcolor: 'primary.main', 
            color: 'primary.contrastText',
            py: 2,
            px: 3,
            '& .MuiTypography-root': {
              fontWeight: 600,
              fontSize: '1.25rem'
            }
          }}>
            {isEditing ? '✏️ Editar Proceso' : '➕ Nuevo Proceso'}
          </DialogTitle>
          <DialogContent sx={{ p: 2, maxHeight: '70vh', overflowY: 'auto' }}>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={3}>
              {/* Columna izquierda */}
              <Box>
                <FormControl fullWidth margin="dense" error={currentProcess.name.trim() === ''}>
                <TextField
                  label="Nombre del Proceso"
                  name="name"
                  value={currentProcess.name}
                  onChange={handleInputChange}
                  required
                  disabled={isEditing}
                  variant="outlined"
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                      '& fieldset': {
                        borderColor: 'divider',
                      },
                      '&:hover fieldset': {
                        borderColor: 'primary.main',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'primary.main',
                        boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.2)'
                      },
                    },
                  }}
                />
                {currentProcess.name.trim() === '' && (
                  <FormHelperText sx={{ ml: 0, mt: 0.5, color: 'error.main' }}>
                    ⚠️ El nombre es requerido
                  </FormHelperText>
                )}
              </FormControl>
              
              <FormControl fullWidth margin="dense" size="small">
                <InputLabel id="process-type-label">Tipo de Proceso</InputLabel>
                <Select
                  labelId="process-type-label"
                  value={currentProcess.type || (processTypes.length > 0 ? processTypes[0].id : 1)}
                  label="Tipo de Proceso"
                  onChange={(e) => {
                    console.log('Tipo seleccionado:', e.target.value);
                    setCurrentProcess({
                      ...currentProcess,
                      type: e.target.value as number
                    });
                  }}
                  sx={{
                    borderRadius: 1,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'divider',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                      boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.2)'
                    },
                  }}
                >
                  {processTypes.length > 0 ? (
                    processTypes.map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        {type.name}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value={1}>
                      Cargando tipos...
                    </MenuItem>
                  )}
                </Select>
                <FormHelperText sx={{ ml: 0, mt: 0.5, color: 'text.secondary' }}>
                  Selecciona el tipo de proceso que se ejecutará
                </FormHelperText>
              </FormControl>
              
              <Box 
                sx={{
                  p: 1.5,
                  mt: 1.5,
                  mb: 1,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.paper'
                }}
              >
                <FormControl fullWidth>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={currentProcess.status === 'active'}
                        onChange={(e) =>
                          setCurrentProcess({
                            ...currentProcess,
                            status: e.target.checked ? 'active' : 'inactive'
                          })
                        }
                        name="status"
                        color="primary"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box 
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: currentProcess.status === 'active' ? 'success.main' : 'error.main',
                            mr: 1
                          }} 
                        />
                        <Typography variant="body1">
                          {currentProcess.status === 'active' ? 'Activo' : 'Inactivo'}
                        </Typography>
                      </Box>
                    }
                    sx={{ m: 0 }}
                  />
                  <FormHelperText sx={{ ml: 4, mt: 0.5, color: 'text.secondary' }}>
                    {currentProcess.status === 'active' 
                      ? 'El proceso está activo y se ejecutará según la programación.' 
                      : 'El proceso está inactivo y no se ejecutará.'}
                  </FormHelperText>
                </FormControl>
              </Box>
              
              <Box 
                sx={{
                  p: 1.5,
                  mt: 1.5,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: !hasDaySelected ? 'error.main' : 'divider',
                  bgcolor: 'background.paper',
                  transition: 'border-color 0.2s ease-in-out'
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle2" fontWeight={500}>
                    Días de ejecución
                  </Typography>
                  {!hasDaySelected && (
                    <Typography variant="caption" color="error" sx={{ display: 'flex', alignItems: 'center' }}>
                      <ErrorOutlineIcon fontSize="small" sx={{ mr: 0.5 }} />
                      Seleccione al menos un día
                    </Typography>
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Selecciona los días en los que se ejecutará el proceso
                </Typography>
                <Box display="grid" gridTemplateColumns="repeat(auto-fill, minmax(80px, 1fr))" gap={1}>
                  {daysOfWeek.map((day) => (
                    <Box 
                      key={day.id}
                      onClick={() => handleDayToggle(day.id)}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 1,
                        height: 60,
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: Boolean(currentProcess[day.id as keyof typeof currentProcess]) 
                          ? 'primary.main' 
                          : 'divider',
                        bgcolor: Boolean(currentProcess[day.id as keyof typeof currentProcess]) 
                          ? 'primary.light' 
                          : 'background.paper',
                        color: Boolean(currentProcess[day.id as keyof typeof currentProcess]) 
                          ? 'primary.contrastText' 
                          : 'text.primary',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          borderColor: 'primary.main',
                          boxShadow: '0 2px 8px rgba(25, 118, 210, 0.15)'
                        }
                      }}
                    >
                      <Typography 
                        variant="body2" 
                        sx={{
                          textAlign: 'center'
                        }}
                      >
                        {day.label}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
              </Box>
              
              {/* Columna derecha */}
              <Box>
              <Box 
                sx={{
                  p: 2,
                  mt: 2,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: time === null ? 'error.main' : 'divider',
                  bgcolor: 'background.paper',
                  transition: 'border-color 0.2s ease-in-out'
                }}
              >
                <Typography variant="subtitle2" fontWeight={500} gutterBottom>
                  Hora de ejecución
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                  <TimePicker
                    label="Seleccionar hora"
                    value={time}
                    onChange={handleTimeChange}
                    ampm={false}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        variant: 'outlined',
                        size: 'small',
                        error: time === null,
                        helperText: time === null ? (
                          <Box component="span" display="flex" alignItems="center">
                            <ErrorOutlineIcon fontSize="small" sx={{ mr: 0.5 }} />
                            La hora es requerida
                          </Box>
                        ) : 'Selecciona la hora de ejecución',
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1,
                            '& fieldset': {
                              borderColor: 'divider',
                            },
                            '&:hover fieldset': {
                              borderColor: 'primary.main',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: 'primary.main',
                              boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.2)'
                            },
                          },
                        }
                      },
                      openPickerButton: {
                        color: 'primary'
                      }
                    }}
                    views={['hours', 'minutes']}
                    minutesStep={5}
                  />
                </LocalizationProvider>
              </Box>

              {/* Campo de emails */}
              <Box 
                sx={{
                  p: 2,
                  mt: 2,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.paper'
                }}
              >
                <Typography variant="subtitle2" fontWeight={500} gutterBottom>
                  Emails de notificación
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Ingresa los emails separados por coma o arrastra usuarios desde la lista
                </Typography>
                
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  value={currentProcess.emails || ''}
                  onChange={handleEmailsChange}
                  placeholder="ejemplo@cocacola.com, otro@cocacola.com"
                  variant="outlined"
                  size="small"
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                      '& fieldset': {
                        borderColor: 'divider',
                      },
                      '&:hover fieldset': {
                        borderColor: 'primary.main',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'primary.main',
                        boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.2)'
                      },
                    },
                  }}
                  InputProps={{
                    startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  }}
                />

                {/* Lista de usuarios activos */}
                <Box>
                  <Typography variant="body2" fontWeight={500} mb={1}>
                    Usuarios activos (arrastra para agregar)
                  </Typography>
                  <Box 
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 1,
                      maxHeight: 120,
                      overflowY: 'auto',
                      p: 1,
                      border: '1px dashed',
                      borderColor: 'divider',
                      borderRadius: 1,
                      bgcolor: 'grey.50'
                    }}
                  >
                    {activeUsers.length > 0 ? (
                      activeUsers.map((user) => (
                        <Box
                          key={user.id}
                          draggable
                          onDragStart={(e) => handleDragStart(e, user)}
                          onClick={() => addEmailFromUser(user)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            px: 1.5,
                            py: 0.5,
                            bgcolor: 'white',
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            cursor: 'grab',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              bgcolor: 'primary.light',
                              borderColor: 'primary.main',
                              transform: 'translateY(-1px)',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            },
                            '&:active': {
                              cursor: 'grabbing',
                              transform: 'translateY(0)'
                            }
                          }}
                        >
                          <DragIndicatorIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                          <PersonIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                          <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                            {user.first_name} {user.last_name}
                          </Typography>
                        </Box>
                      ))
                    ) : (
                      <Typography variant="caption" color="text.disabled" sx={{ p: 1 }}>
                        No hay usuarios activos disponibles
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Button 
              onClick={handleCloseDialog} 
              color="inherit"
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                '&:hover': {
                  bgcolor: 'action.hover'
                }
              }}
            >
              Cancelar
            </Button>
            <Button 
              type="submit" 
              color="primary" 
              variant="contained"
              disabled={!isFormValid}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                '&:disabled': {
                  bgcolor: 'action.disabledBackground',
                  color: 'text.disabled'
                },
                '&:hover': {
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                },
                transition: 'all 0.2s ease-in-out'
              }}
              startIcon={isFormValid ? <SaveIcon /> : <BlockIcon />}
            >
              {isEditing ? 'Guardar cambios' : 'Crear proceso'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Container>
  );
};

export default ProcesosPage;
